import * as THREE from "three";
import { facet, GLOW_ATTRIBUTE } from "./facet";
import { merge } from "./kit";

// The part sink the building kit draws with (spike 002 C4): every part is a shaped primitive faceted in a palette
// colour (meshkit's `facet`), carrying three extra per-vertex numbers the low-poly look reads:
//   glow  0 lit, 1 always emissive (a forge's coals);
//   lit   0..1, how much the part glows at night (a window, a lamp): the look scales it by its night factor;
//   cut   which cut-away group the part belongs to, and whose: owner × CUT_OWNER_STRIDE + group. A look that cuts away a
//         building's roof and near walls (seen from inside) discards the faces of the groups it is told, for one owner.
// A transform stack places parts inside furniture and other sub-frames. Same rng, same parts.

/** The night-glow attribute (one float per vertex, 0..1). */
export const LIT_ATTRIBUTE = "lit";
/** The cut-away attribute (one float per vertex: owner × CUT_OWNER_STRIDE + group). */
export const CUT_ATTRIBUTE = "cut";
/** Groups per owner in the cut attribute. */
export const CUT_OWNER_STRIDE = 8;
/** The cut groups: never cut, the roof (with gables and chimney), and the four walls by their outward side in the
 *  piece's own frame. */
export const CUT_GROUPS = { none: 0, roof: 1, px: 2, nx: 3, pz: 4, nz: 5 } as const;
export type CutGroup = (typeof CUT_GROUPS)[keyof typeof CUT_GROUPS];

/** How one part looks. */
export interface PartStyle {
  /** Metres of vertex jitter (weathered stone); default 0 (crisp carpentry). */
  jitter?: number;
  /** Per-face tint (default the sink's). */
  tint?: number;
  glow?: number;
  lit?: number;
  cut?: number;
}

/** A colour: a palette key or a THREE colour. */
export type KitColour = string | THREE.Color;

/** Collects faceted parts into one geometry. */
export class Parts {
  private readonly geos: THREE.BufferGeometry[] = [];
  private readonly stack: THREE.Matrix4[] = [new THREE.Matrix4()];

  constructor(
    private readonly rng: () => number,
    private readonly palette: (c: string) => THREE.Color,
    private readonly tint = 0.05,
  ) {}

  /** The palette's colour for a key (or the colour itself). */
  colour(c: KitColour): THREE.Color {
    return typeof c === "string" ? this.palette(c) : c;
  }

  /** Run `fn` with parts placed at (x, y, z) turned by `yaw` inside the current frame. */
  at(x: number, y: number, z: number, yaw: number, fn: () => void): void {
    const m = this.top().clone().multiply(new THREE.Matrix4().makeRotationY(yaw).setPosition(x, y, z));
    this.stack.push(m);
    try {
      fn();
    } finally {
      this.stack.pop();
    }
  }

  /** The current frame's matrix. */
  top(): THREE.Matrix4 {
    return this.stack[this.stack.length - 1]!;
  }

  /** Add any geometry (in the current frame), faceted in `colour`. */
  add(geo: THREE.BufferGeometry, colour: KitColour, s: PartStyle = {}): void {
    const g = facet(geo, { colour: this.colour(colour), jitter: s.jitter ?? 0, tint: s.tint ?? this.tint, glow: s.glow ?? 0, rng: this.rng });
    g.applyMatrix4(this.top());
    const n = g.getAttribute("position").count;
    g.setAttribute(LIT_ATTRIBUTE, new THREE.BufferAttribute(new Float32Array(n).fill(s.lit ?? 0), 1));
    g.setAttribute(CUT_ATTRIBUTE, new THREE.BufferAttribute(new Float32Array(n).fill(s.cut ?? 0), 1));
    this.geos.push(g);
    geo.dispose();
  }

  /** A box of size (w, h, d) centred at (x, y, z), turned by Euler (rx, ry, rz). */
  box(w: number, h: number, d: number, x: number, y: number, z: number, colour: KitColour, s: PartStyle = {}, rx = 0, ry = 0, rz = 0): void {
    const g = new THREE.BoxGeometry(w, h, d);
    if (rx || ry || rz) g.applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rx, ry, rz)));
    g.translate(x, y, z);
    this.add(g, colour, s);
  }

  /** A vertical cylinder (radius top and bottom, height h, `seg` sides) with its base at (x, y, z). */
  cyl(rt: number, rb: number, h: number, seg: number, x: number, y: number, z: number, colour: KitColour, s: PartStyle = {}): void {
    const g = new THREE.CylinderGeometry(rt, rb, h, seg);
    g.translate(x, y + h / 2, z);
    this.add(g, colour, s);
  }

  /** A prism: the outline `pts` (in the x–y plane) pushed `depth` along z, centred on z = 0, then moved. */
  prism(pts: readonly [number, number][], depth: number, colour: KitColour, s: PartStyle = {}, place?: THREE.Matrix4): void {
    const shape = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y)));
    const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, steps: 1, curveSegments: 1 });
    g.translate(0, 0, -depth / 2);
    if (place) g.applyMatrix4(place);
    this.add(g, colour, s);
  }

  /** How many parts so far. */
  get count(): number {
    return this.geos.length;
  }

  /** Every part merged (position, normal, color, glow, lit, cut), or an empty geometry with those attributes. */
  geometry(): THREE.BufferGeometry {
    if (this.geos.length === 0) return emptyKitGeometry();
    const g = merge(this.geos);
    for (const x of this.geos) x.dispose();
    this.geos.length = 0;
    return g;
  }
}

/** A geometry with the kit's attributes and no triangles. */
export function emptyKitGeometry(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  for (const [k, n] of [
    ["position", 3],
    ["normal", 3],
    ["color", 3],
    [GLOW_ATTRIBUTE, 1],
    [LIT_ATTRIBUTE, 1],
    [CUT_ATTRIBUTE, 1],
  ] as const)
    g.setAttribute(k, new THREE.BufferAttribute(new Float32Array(0), n));
  return g;
}
